import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import { useLocale } from '../../contexts/LocaleContext';
import { TRANSLATION_CONSTANTS } from '../../app-consts/translations';
import {
    Grid,
    Type,
    FormField,
    Flex,
    Button,
    Tooltip,
    Icon
} from 'mesh-component-library';
import { QuestionCircleIcon } from 'mesh-icon-library';
import { generatePath } from '../../utilities/RedirectUtils';
import { refreshReducersToInitial, updateCurrentMileage, updateServiceTransition } from '../../action';
import {
    getExpirationMileageByIndex,
    getLastKnownMileageByIndex,
    getCallIdFromCommonBenefits,
    getPartnerDetails,
    getServiceRequestPayload
} from '../../selector';
import { UNABLE_TO_COMPLETE_SERVICE } from '../../app-consts/errorMessageConstants';
import { validateOdometerReading } from '../../utils/odometerValidation';
import { getWarrantyDeflectionOemNumber } from '../../features/WarrantyDeflection/selectors';
import { submitOdometerPartialSaveRequest } from '../../features/WarrantyDeflection/actions';
import { ERU, WRD } from '../../app-consts/appConstants';


const OdometerLookup = ({ history, setCurrentLookup }) => {
    const [odometer, setOdometer] = useState('');

    const getTranslatedText = useLocale();
    const expirationMileage = useSelector((state) => getExpirationMileageByIndex(state, 0));
    const lastKnownMileage = useSelector((state) => getLastKnownMileageByIndex(state, 0));
    const callId = useSelector((state) => getCallIdFromCommonBenefits(state));
    const warrantyDeflectionOemNumber = useSelector(getWarrantyDeflectionOemNumber);
    const partnerCode = useSelector((state) => getPartnerDetails(state)?.partnerCode);
    const serviceRequestPayload = useSelector(getServiceRequestPayload);
    const dispatch = useDispatch();
    const isWheelsPartner = partnerCode === WRD;
    const isEmkayPartner = partnerCode === ERU;
    const enterValidMileageMessage =
        isWheelsPartner
            ? getTranslatedText(TRANSLATION_CONSTANTS.WHEELS_MILEAGE_MESSAGE)
            : getTranslatedText(TRANSLATION_CONSTANTS.ENTER_VALID_MILEAGE);

    const handleChange = (event) => {
        setOdometer(event.target.value.trim());
    }

    // For the warranty deflection flow and for any active Wheels/Emkay membership, the entered
    // mileage must be strictly greater than lastKnownMileage when a lastKnownMileage is
    // available. Evaluated live (not just on submit) so the Continue button and help text
    // respond as the customer types.
    const isMileageNotGreaterThanLastKnown = !!(
        (warrantyDeflectionOemNumber || isWheelsPartner || isEmkayPartner) &&
        (lastKnownMileage || lastKnownMileage === 0) &&
        odometer?.length > 0 &&
        parseInt(odometer) <= lastKnownMileage
    );

    const validateCompleteForm = () => {
        let odometerAvailable = odometer?.length > 0 && odometer.length <= 7;
        if (odometerAvailable) {
            return !odometerAvailable || isMileageNotGreaterThanLastKnown;
        }
        return true;
    };

    const continueClick = () => {
        const reading = parseInt(odometer);

        // Shared by both the OEM warranty deflection and Wheels active-membership submit
        // paths below: stores the reading and kicks off the odometer partial save. Callers
        // are responsible for their own post-dispatch navigation.
        const submitOdometerReading = () => {
            dispatch(updateCurrentMileage(reading));
            dispatch(
                submitOdometerPartialSaveRequest({
                    serviceRequestPayload,
                    partnerCode,
                    odometerReading: reading,
                    history,
                    dispatch: () => dispatch(refreshReducersToInitial())
                })
            );
        };

        // Warranty deflection trigger: requires the reading to be strictly greater than
        // lastKnownMileage (when available); no comparison against expirationMileage.
        // Advances to the OEM Deflection screen.
        if (warrantyDeflectionOemNumber) {
            if (isMileageNotGreaterThanLastKnown) {
                return;
            }
            submitOdometerReading();
            setCurrentLookup('oemDeflection');
            return;
        }

        // Any active Wheels membership (without an OEM warranty deflection reading): still
        // submit a partial save with the odometer reading, but there's no OEM Deflection screen
        // to advance to and no expirationMileage range to validate against, so continue to Home.
        if (isWheelsPartner || isEmkayPartner) {
            if (isMileageNotGreaterThanLastKnown) {
                return;
            }
            submitOdometerReading();
            history.push(generatePath('home'));
            return;
        }

        const { isValid } = validateOdometerReading(reading, lastKnownMileage, expirationMileage);
        if (isValid) {
            dispatch(updateCurrentMileage(reading));
            history.push(generatePath('home'));
        } else {
            datadogRum.addError(new Error('Odometer reading out of valid range'), {
                odometerReading: reading,
                lastKnownMileage,
                expirationMileage,
                callId
            });
            dispatch(
                updateServiceTransition({
                    errorMessageContent: UNABLE_TO_COMPLETE_SERVICE
                })
            );
            history.push(generatePath("ServiceTransition"));
        }
    };


    return (
        <>
            <Grid
                utils={{
                    vrBottom: "md"
                }}>
                <Grid.Column>
                    <Type
                        id=""
                        element="h1"
                        weight="semibold"
                        align="left"
                        size="6xl"
                        lineHeight="md"
                        utils={{
                            vrTop: "md"
                        }}>
                        {getTranslatedText(TRANSLATION_CONSTANTS.PROVIDE_VEHICLE_INFO)}
                    </Type>
                </Grid.Column>
            </Grid>
            <Grid utils={{
                vrBottom: "md"
            }}>
                <Grid.Column>
                    <FormField
                        size="md"
                        id=""
                        label={<Flex alignItems="stretch" justifyContent="between" isBlock={true}>
                            <Type size="lg" weight="bold" emphasis="information">
                                {getTranslatedText(TRANSLATION_CONSTANTS.ODOMETER)}
                            </Type>
                            <Tooltip
                                id="mileage-tooltip"
                                size="lg"
                                text={getTranslatedText(TRANSLATION_CONSTANTS.CAPTURE_ACTUAL_MILEAGE)}
                            >
                                <Icon icon={QuestionCircleIcon} color="#0033A0"
                                    size={1.2} />
                            </Tooltip>
                        </Flex>}
                        labelProps={{
                            style: {
                                width: "100%"
                            }
                        }}
                        value={odometer}
                        onChange={handleChange}
                        utils={{
                            vrTop: "xs",
                            vrBottom: "xs"
                        }}
                        required
                        pattern="d*"
                        inputMode="numeric"
                        maxLength={7}
                        type="number"
                        hasError={isMileageNotGreaterThanLastKnown}
                        errorText={isWheelsPartner ? enterValidMileageMessage : undefined}
                        data-testid="odometer"
                    />
                </Grid.Column>
            </Grid>
            <Grid utils={{
                vrBottom: "md"
            }}>
                <Grid.Column>
                    <Button
                        hasUpgrade
                        id=""
                        disabled={validateCompleteForm()}
                        onClick={continueClick}
                        utils={{ fullWidth: true }}>
                        {getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE)}
                    </Button>
                </Grid.Column>
            </Grid>
        </>
    )
};

export default OdometerLookup;
